import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Eye,
  Lock,
  LockOpen,
  MessageSquare,
  Pencil,
  Play,
  Repeat,
  Send,
  ShieldAlert,
  Unlock,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Attachment, type DepartmentRef, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDateTime, formatDuration, formatTime } from '../lib/format';
import { SlaCountdown } from '../components/SlaCountdown';
import { AttachmentComposer, AttachmentList, AttachmentsCard, DropZone, useUploads } from '../components/Attachments';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Detail,
  ErrorState,
  Field,
  LoadingBlock,
  PageHeader,
  Select,
  StatusDot,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  priorityTone,
  statusTone,
  useToast,
} from '../components/ui';

export function TicketDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();


  // Opening this page IS the view event (§10.1) — the GET records it server-side.
  const ticketQuery = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get<any>(`/tickets/${id}`),
  });

  const slaQuery = useQuery({
    queryKey: ['ticket', id, 'sla'],
    queryFn: () => api.get<any>(`/tickets/${id}/sla`),
    refetchInterval: 30_000,
  });

  const timelineQuery = useQuery({
    queryKey: ['ticket', id, 'timeline'],
    queryFn: () => api.get<any[]>(`/tickets/${id}/timeline`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.post<any>(`/tickets/${id}/${path}`, body),
    onSuccess: () => {
      invalidate();
      navigate(`/tickets/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (ticketQuery.isLoading) return <LoadingBlock />;
  if (ticketQuery.error) return <ErrorState error={ticketQuery.error} onRetry={() => void ticketQuery.refetch()} />;

  const ticket = ticketQuery.data;
  const sla = slaQuery.data;
  const isOpen = !['CLOSED', 'CANCELLED', 'RESOLVED'].includes(ticket.status);
  const lockedByOther = ticket.lockActive && ticket.lock?.lockedByUserId !== user?.id;
  // The server's rules for this person: edit until started; only the assignee,
  // a team leader or an admin starts, moves and finishes it.
  const may = ticket.can ?? {};

  return (
    <>
      <div className="mb-4">
        <Link
          to="/tickets"
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('tickets.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{ticket.number}</span>
            <span>{ticket.subject}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(ticket.status)}>{enumLabel(ticket.status, locale)}</Badge>
            <Badge tone={priorityTone(ticket.priority)}>{enumLabel(ticket.priority, locale)}</Badge>
            <span className="text-muted">
              {ticket.client ? `${clientLabel(ticket.client, locale)} · ` : ''}
              {localized(ticket.targetDepartment)} ·{' '}
              {ticket.project ? localized(ticket.project) : t('common.general')}
            </span>
            {ticket.requesterAccountId && <Badge tone="plasma">{t('tickets.raisedOnPortal')}</Badge>}
          </span>
        }
        action={
          <>
            {may.edit && (
              <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/tickets/${id}/edit`)}>
                {t('common.edit')}
              </Button>
            )}
            {isOpen && !ticket.assignedUserId && can(P.TICKETS_HANDLE) && (
              <Button
                variant="primary"
                icon={<Check className="h-4 w-4" />}
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'claim' })}
              >
                {t('tickets.assignToMe')}
              </Button>
            )}
            {may.start && (
              <Button
                variant="primary"
                icon={<Play className="h-4 w-4" />}
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'status', body: { status: 'IN_PROGRESS' } })}
              >
                {t('tickets.start')}
              </Button>
            )}
            {(may.assign || may.reassign) && (
              <Button onClick={() => navigate(`/tickets/${id}/${may.reassign ? 'reassign' : 'assign'}`)}>
                {may.reassign ? t('tickets.reassign') : t('tickets.assign')}
              </Button>
            )}
            {may.resolve && (
              <Button variant="signal" onClick={() => navigate(`/tickets/${id}/resolve`)}>
                {t('tickets.resolve')}
              </Button>
            )}
            {['RESOLVED', 'CLOSED'].includes(ticket.status) && (
              <Button onClick={() => navigate(`/tickets/${id}/reopen`)}>{t('tickets.reopen')}</Button>
            )}
          </>
        }
      />

      {/* §14.1 — the soft lock banner. */}
      {ticket.lockActive && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/35 bg-warning/10 px-4 py-2.5">
          <span className="flex items-center gap-2 text-body-sm text-warning-fg dark:text-warning-dark">
            <Lock className="h-4 w-4" aria-hidden />
            <strong>{ticket.lock.user.fullName}</strong> {t('tickets.lockedBy')}
            <span className="text-faint">
              ({t('common.to')} {formatTime(ticket.lock.expiresAt, locale)})
            </span>
          </span>
          {lockedByOther && can(P.TICKETS_ASSIGN) && (
            <Button
              size="sm"
              icon={<Unlock className="h-4 w-4" />}
              onClick={async () => {
                await api.post(`/tickets/${id}/unlock?force=true`, { reason: 'Released by a team leader.' });
                invalidate();
              }}
            >
              {t('tickets.forceUnlock')}
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-3">
        {/* ---- Left: SLA, conversation, timeline --------------------------- */}
        <div className="space-y-5 xl:col-span-2">
          {/* §37.3 — both clocks, on server time. */}
          {sla && (
            <Card>
              <CardHeader
                title="SLA"
                subtitle={`${t('tickets.viewSla')} ${sla.viewSlaMinutes}m · ${t('tickets.assignmentSla')} ${sla.assignmentSlaMinutes}m`}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-md border hairline p-3">
                  <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('tickets.viewSla')}</p>
                  <SlaCountdown
                    secondsRemaining={sla.viewSecondsRemaining}
                    measuredAt={sla.now}
                    met={sla.viewMet}
                    stopped={ticket.status === 'CANCELLED'}
                    processed={!!sla.viewSlaProcessedAt}
                  />
                  <p className="mt-2 text-caption text-muted tabular">
                    {t('violations.deadline')}: {formatDateTime(sla.viewDeadlineAt, locale)}
                  </p>
                </div>
                <div className="rounded-md border hairline p-3">
                  <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">
                    {t('tickets.assignmentSla')}
                  </p>
                  <SlaCountdown
                    secondsRemaining={sla.assignmentSecondsRemaining}
                    measuredAt={sla.now}
                    met={sla.assignmentMet}
                    stopped={ticket.status === 'CANCELLED'}
                    processed={!!sla.assignmentSlaProcessedAt}
                  />
                  <p className="mt-2 text-caption text-muted tabular">
                    {sla.assignedAt
                      ? `${t('tickets.assign')}: ${formatDateTime(sla.assignedAt, locale)}`
                      : `${t('violations.deadline')}: ${formatDateTime(sla.assignmentDeadlineAt, locale)}`}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* §10.3 — the immutable responsibility snapshot. */}
          {sla?.responsibility?.length > 0 && (
            <Card padded={false}>
              <CardHeader
                title={t('tickets.responsibility')}
                subtitle={t('tickets.snapshotNote')}
                className="p-5 pb-0"
              />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('tickets.onShiftAtCreation')}</Th>
                    <Th>{t('tickets.eligible')}</Th>
                    <Th align="end">{t('tickets.views')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {sla.responsibility.map((row: any) => (
                    <Tr key={row.userId}>
                      <Td>{localized(row.user)}</Td>
                      <Td>
                        <span className="flex items-center gap-2">
                          <StatusDot tone={row.onShiftAtCreation ? 'online' : 'offline'} />
                          <span className="text-muted">
                            {row.onShiftAtCreation ? t('common.yes') : t('common.no')}
                          </span>
                        </span>
                      </Td>
                      <Td>
                        {row.eligibleForViewSla ? (
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <Badge tone="brand">{t('tickets.eligible')}</Badge>
                            {/* Responsible by support mode alone, off shift (2026-11-01): say so. */}
                            {row.reason === 'SUPPORT_MODE' && <Badge tone="plasma">{t('shifts.support')}</Badge>}
                          </span>
                        ) : (
                          <Badge tone="neutral" title={row.reason}>
                            {enumLabel(row.reason, locale)}
                          </Badge>
                        )}
                      </Td>
                      <Td align="end">
                        {row.firstViewedAt ? (
                          <span className="inline-flex flex-col items-end">
                            <Badge tone={row.viewedOnTime ? 'success' : 'error'}>
                              {row.viewedOnTime ? t('tickets.viewedOnTime') : t('tickets.viewedLate')}
                            </Badge>
                            <span className="mt-0.5 text-caption text-faint tabular">
                              +{formatDuration(row.firstViewDelaySeconds)}
                            </span>
                          </span>
                        ) : (
                          <Badge tone={row.eligibleForViewSla ? 'error' : 'neutral'}>{t('tickets.notViewed')}</Badge>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          <AttachmentsCard
            attachments={ticket.attachments}
            basePath={`/tickets/${id}`}
            queryKey={['ticket', id]}
            canRemove={(a: Attachment) => a.file.uploadedBy?.id === user?.id || can(P.TICKETS_ASSIGN)}
          />

          <Conversation ticket={ticket} onPosted={invalidate} />

          {/* §27.3 — the timeline. */}
          <Card>
            <CardHeader title={t('tickets.timeline')} />
            {timelineQuery.data?.length ? (
              <ol className="relative space-y-0">
                {timelineQuery.data.map((event, index) => (
                  <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                    <span className="relative flex flex-col items-center">
                      <span
                        className={
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full ' +
                          (event.action.startsWith('sla.') ? 'bg-error' : 'bg-primary')
                        }
                        aria-hidden
                      />
                      {index < timelineQuery.data.length - 1 && (
                        <span className="w-px flex-1 bg-[var(--line)]" aria-hidden />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-mono text-mono-sm text-[var(--text-tertiary)] tabular">
                          {formatTime(event.at, locale)}
                        </span>
                        <span className="text-body-sm font-medium text-[var(--text-primary)]">
                          {humanizeAction(event.action)}
                        </span>
                        {event.actor && <span className="text-caption text-muted">· {localized(event.actor)}</span>}
                      </div>
                      {event.message && <p className="mt-0.5 text-body-sm text-muted">{event.message}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-body-sm text-muted">—</p>
            )}
          </Card>
        </div>

        {/* ---- Right: details, violations, actions -------------------------- */}
        <div className="space-y-5">
          <Card>
            <CardHeader title={t('tickets.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('tickets.client')}>
                {ticket.client ? (
                  can(P.CLIENTS_VIEW) ? (
                    <Link to={`/clients/${ticket.client.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                      {clientLabel(ticket.client, locale)}
                    </Link>
                  ) : (
                    clientLabel(ticket.client, locale)
                  )
                ) : (
                  t('tickets.noClient')
                )}
              </Detail>
              {/* Who at the client asked for it — every ticket we raise names one (2026-10-09). */}
              {!ticket.requesterAccountId && (
                <Detail label={t('tickets.contact')}>
                  {ticket.contact ? (
                    <span className="inline-flex flex-col items-end">
                      <span>{ticket.contact.name}</span>
                      {(ticket.contact.jobTitle || ticket.contact.deletedAt) && (
                        <span className="text-caption text-muted">
                          {[ticket.contact.jobTitle, ticket.contact.deletedAt && t('tickets.contactRemoved')]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </Detail>
              )}
              <Detail label={t('tickets.raisedBy')}>
                {ticket.requester ? (
                  localized(ticket.requester)
                ) : ticket.requesterAccount?.contact ? (
                  // One of the client's contacts, with their own portal sign-in (2026-10-11).
                  <span className="inline-flex flex-col items-end gap-1">
                    <span>{ticket.requesterAccount.contact.name}</span>
                    <span className="inline-flex flex-wrap items-center justify-end gap-1.5 text-caption text-muted">
                      {[ticket.requesterAccount.contact.jobTitle, clientLabel(ticket.client, locale)]
                        .filter(Boolean)
                        .join(' · ')}
                      <Badge tone="plasma">{t('tickets.raisedOnPortal')}</Badge>
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                    {clientLabel(ticket.client, locale)}
                    <Badge tone="plasma">{t('tickets.raisedOnPortal')}</Badge>
                  </span>
                )}
              </Detail>
              <Detail label={t('common.assignee')}>
                {ticket.assignee ? localized(ticket.assignee) : t('common.unassigned')}
              </Detail>
              <Detail label={t('common.department')}>{localized(ticket.targetDepartment)}</Detail>
              <Detail label={t('common.project')}>
                {ticket.project ? (
                  <Link to={`/projects/${ticket.project.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                    {localized(ticket.project)}
                  </Link>
                ) : (
                  t('common.general')
                )}
              </Detail>
              <Detail label={t('tickets.category')}>
                {ticket.category ? localized(ticket.category) : '—'}
              </Detail>
              <Detail label={t('common.createdAt')}>{formatDateTime(ticket.createdAt, locale)}</Detail>
              {ticket.assignedAt && (
                <Detail label={t('tickets.assign')}>{formatDateTime(ticket.assignedAt, locale)}</Detail>
              )}
              {ticket.resolvedAt && (
                <Detail label={t('tickets.resolve')}>{formatDateTime(ticket.resolvedAt, locale)}</Detail>
              )}
            </dl>

            {ticket.description && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.description')}</p>
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]">{ticket.description}</p>
              </div>
            )}

            {ticket.resolution && (
              <div className="mt-4 rounded-md border border-success/30 bg-success/8 p-3">
                <p className="mb-1 text-caption uppercase text-success-fg dark:text-success">{t('tickets.resolution')}</p>
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]">{ticket.resolution}</p>
              </div>
            )}
          </Card>

          {ticket.violations?.length > 0 && (
            <Card>
              <CardHeader
                title={t('violations.violations')}
                action={<Badge tone="error">{ticket.violations.length}</Badge>}
              />
              <ul className="space-y-2">
                {ticket.violations.map((v: any) => (
                  <li
                    key={v.id}
                    className="flex items-start justify-between gap-2 rounded-md border border-error/25 bg-error/6 p-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-body-sm font-medium text-error-fg dark:text-error-dark">{enumLabel(v.type, locale)}</p>
                      <p className="text-caption text-muted">
                        {v.user ? v.user.fullName : t('common.department')} ·{' '}
                        {formatDateTime(v.occurredAt, locale)}
                      </p>
                    </div>
                    <Badge tone={v.status === 'VOIDED' ? 'neutral' : 'error'}>
                      {enumLabel(v.status, locale)}
                    </Badge>
                  </li>
                ))}
              </ul>
              <Link
                to="/violations"
                className="mt-3 inline-flex items-center gap-1.5 text-body-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {t('violations.title')}
              </Link>
            </Card>
          )}

          <Card>
            <CardHeader title={t('common.actions')} />
            <div className="flex flex-wrap gap-2">
              {!ticket.lockActive && can(P.TICKETS_HANDLE) && (
                <Button
                  size="sm"
                  icon={<Lock className="h-4 w-4" />}
                  onClick={async () => {
                    await api.post(`/tickets/${id}/lock`);
                    invalidate();
                  }}
                >
                  {t('tickets.lock')}
                </Button>
              )}
              {ticket.lockActive && ticket.lock?.lockedByUserId === user?.id && (
                <Button
                  size="sm"
                  icon={<LockOpen className="h-4 w-4" />}
                  onClick={async () => {
                    await api.post(`/tickets/${id}/unlock`, {});
                    invalidate();
                  }}
                >
                  {t('common.clear')}
                </Button>
              )}
              {can(P.TICKETS_ASSIGN) && isOpen && (
                <Button size="sm" icon={<Repeat className="h-4 w-4" />} onClick={() => navigate(`/tickets/${id}/reroute`)}>
                  {t('tickets.reroute')}
                </Button>
              )}
              {may.statuses?.length > 0 && (
                <Button size="sm" onClick={() => navigate(`/tickets/${id}/status`)}>
                  {t('common.status')}
                </Button>
              )}
            </div>
            <p className="mt-3 text-caption text-faint">
              A lock is a courtesy to other handlers — it never pauses either SLA clock (§14.3).
            </p>
          </Card>
        </div>
      </div>

    </>
  );
}

/**
 * The replies. Words, files and voice notes, in any mix. Internal notes — and
 * the files on them — are the handlers' only: someone who only raised the
 * ticket never receives them, and is not offered the switch to write one.
 */
function Conversation({ ticket, onPosted }: { ticket: any; onPosted: () => void }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const uploads = useUploads();
  const canPost = (body.trim() !== '' || uploads.fileIds.length > 0) && !uploads.busy;

  const post = useMutation({
    mutationFn: () =>
      api.post(`/tickets/${ticket.id}/reply`, {
        body: body.trim() || undefined,
        isInternal: ticket.canSeeInternal && internal,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      setBody('');
      uploads.clear();
      onPosted();
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader
        title={t('tickets.conversation')}
        action={<Badge tone="neutral">{ticket.comments?.length ?? 0}</Badge>}
      />

      {ticket.comments?.length ? (
        <ul className="mb-4 space-y-3">
          {ticket.comments.map((comment: any) => (
            <li
              key={comment.id}
              className={
                'rounded-md border p-3 ' +
                (comment.isInternal ? 'border-signal/30 bg-signal/8' : 'border-[var(--line)]')
              }
            >
              <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <span className="text-body-sm font-medium text-[var(--text-primary)]">
                  {comment.author
                    ? localized(comment.author)
                    : comment.authorAccount?.contact
                      ? `${comment.authorAccount.contact.name} · ${clientLabel(ticket.client, locale)}`
                      : clientLabel(ticket.client, locale)}
                </span>
                {comment.authorAccountId && <Badge tone="plasma">{t('tickets.clientBadge')}</Badge>}
                <span className="text-caption text-faint">{formatDateTime(comment.createdAt, locale)}</span>
                {comment.isInternal && <Badge tone="warning">{t('tickets.internalNote')}</Badge>}
              </div>
              {comment.body && (
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {comment.body}
                </p>
              )}
              <AttachmentList attachments={comment.attachments} className={comment.body ? 'mt-2' : undefined} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-body-sm text-muted">—</p>
      )}

      {ticket.client && (
        <p className="mb-2 text-caption text-muted">{t('tickets.clientSeesReplies')}</p>
      )}
      <DropZone uploads={uploads}>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onPaste={uploads.onPaste}
          placeholder={t('tickets.reply')}
          aria-label={t('tickets.reply')}
          className="min-h-[80px]"
        />
        <AttachmentComposer
          uploads={uploads}
          className="mt-2"
          actions={
            <>
              {ticket.canSeeInternal && (
                <Checkbox
                  checked={internal}
                  onChange={(e) => setInternal(e.target.checked)}
                  label={t('tickets.internalNote')}
                />
              )}
              <Button
                variant="primary"
                size="sm"
                icon={<Send className="h-4 w-4 flip-rtl" />}
                disabled={!canPost}
                loading={post.isPending}
                onClick={() => post.mutate()}
              >
                {t('tickets.reply')}
              </Button>
            </>
          }
        />
      </DropZone>
    </Card>
  );
}

/** The client's name in the reader's language. */
function clientLabel(
  client: { companyName: string; companyNameAr?: string | null } | null | undefined,
  locale: string,
): string {
  if (!client) return '—';
  return locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName;
}

function humanizeAction(action: string): string {
  const map: Record<string, string> = {
    'ticket.created': 'Ticket created',
    'ticket.created_on_portal': 'Raised on the client portal',
    'ticket.updated': 'Edited',
    'ticket.client_replied': 'The client replied',
    'ticket.routed': 'Routed to department',
    'ticket.viewed': 'Viewed',
    'ticket.assigned': 'Assigned',
    'ticket.self_assigned': 'Self-assigned',
    'ticket.reassigned': 'Reassigned',
    'ticket.rerouted': 'Rerouted',
    'ticket.status_changed': 'Status changed',
    'ticket.replied': 'Reply posted',
    'ticket.internal_note': 'Internal note',
    'ticket.resolved': 'Resolved',
    'ticket.reopened': 'Reopened',
    'ticket.force_unlocked': 'Lock forcibly released',
    'ticket.files_attached': 'Files attached',
    'ticket.attachment_removed': 'File removed',
    'sla.view_checked': 'View SLA checked',
    'sla.assignment_violation': 'Assignment SLA violation',
  };
  return map[action] ?? action;
}
